/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions, OneTimePayFeatureFacade } from '@amfam/billing/one-time-pay/feature';
import { PaymentMethodFeatureActions } from '@amfam/billing/payment-method/feature';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';

import { ReviewPaymentWrapperComponent } from '../containers/review-payment-wrapper/review-payment-wrapper.component';

@Injectable()
export class OneTimePayGuard {
  dataLoading$: Observable<boolean> = of(false);

  constructor(
    private store: Store,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private router: Router,
    private feature: FeatureFlagService
  ) {}

  canActivate(): Observable<boolean> {
    // redirect user to overview screen if refresh happens in the middle of OTP flow
    this.router.events
      .pipe(
        filter((rs): rs is NavigationEnd => rs instanceof NavigationEnd),
        filter(event => event.id === 1),
        take(1)
      )
      .subscribe(() => {
        // when the page is refreshd, go back to overview
        this.store.dispatch(fromRouterActions.Go({ path: ['/overview'] }));
        return false;
      });

    return combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyPrefsLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(AutoPaySelectors.getAnyLoading),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingPolicyList)
    ]).pipe(
      map(
        ([
          billAccountsAnyPrefsLoading,
          billAccountsLoading,
          automaticPaymentsLoading,
          scheduledPaymentsLoading,
          policyRiskLoading
        ]) =>
          billAccountsAnyPrefsLoading ||
          billAccountsLoading ||
          automaticPaymentsLoading ||
          scheduledPaymentsLoading ||
          policyRiskLoading
      ),
      filter(loading => !loading),
      withLatestFrom(this.oneTimePayFacade.getOTPEntities()),
      map(([, otpEntities]) => {
        if (!Object.keys(otpEntities)?.length && !this.feature.isEnabled('simplified_payment_p2')) {
          this.oneTimePayFacade.initSingleOneTimePay();
        }
        return true;
      })
    );
  }

  canDeactivate(
    component: ReviewPaymentWrapperComponent,
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    nextState: RouterStateSnapshot
  ): boolean {
    let isHsPolicy;
    const stop$ = new Subject<void>();
    this.store
      .select(BillAccountsSelectors.selectIsSelectedBillPolicyHomesite)
      .pipe(takeUntil(stop$))
      .subscribe(isHSPolicy => {
        isHsPolicy = isHSPolicy;
      });

    const isHSPaymentEnabled = this.feature.isEnabled('one-time-pay-homesite');

    // If the list contains an unassociated bill account, reload the bill accounts list
    if (nextState.url.indexOf('one-time-pay') === -1) {
      this.store.dispatch(OneTimePayActions.resetOneTimePaySetup());
    } else if (
      state.url.includes('one-time-pay/confirmation') &&
      (nextState.url.includes('one-time-pay/choose-accounts') ||
        nextState.url.includes('one-time-pay/review-payment'))
    ) {
      this.store.dispatch(OneTimePayActions.resetOneTimePaySetup());
    }

    if (isHSPaymentEnabled && isHsPolicy) {
      this.store.dispatch(PaymentMethodFeatureActions.resetPaymentMethodState());
    }

    return true;
  }
}
